import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import PricingCards from "@/components/PricingCards";
import CalculatorSection from "@/components/CalculatorSection";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
  ArrowRight, ShieldCheck, TrendingUp, FileCheck, Building2, Check, X,
  Leaf, Landmark, Briefcase, PiggyBank, ScrollText,
  ClipboardList, Calculator, FileSignature, PartyPopper,
  BookOpen, Headset, Lock, Clock, MapPin, Sparkles, Camera,
} from "lucide-react";

const partners = [
  { icon: Landmark, name: "FedTax" },
  { icon: Briefcase, name: "WorkSuite" },
  { icon: PiggyBank, name: "SaveWell" },
  { icon: ScrollText, name: "FormFlow" },
  { icon: Building2, name: "Northgate" },
];

const steps = [
  {
    icon: Calculator,
    title: "Estimate your savings",
    body: "Run the free calculator to see if an S-Corp election will actually lower your self-employment taxes — in about 60 seconds.",
  },
  {
    icon: ClipboardList,
    title: "Pick your path",
    body: "Choose Self-Guided, Assisted, or Full-Service depending on how much help you want. Upgrade any time.",
  },
  {
    icon: FileSignature,
    title: "File Form 2553",
    body: "Follow the step-by-step guide yourself, get expert review, or let our specialists prepare and file everything for you.",
  },
  {
    icon: PartyPopper,
    title: "Start saving",
    body: "Set a reasonable salary, run payroll the right way, and keep more of what you earn — year after year.",
  },
];

const whyUs = [
  {
    icon: TrendingUp,
    title: "Real, defensible savings",
    body: "We model your reasonable-salary range and break-even so you know the election makes sense before you file.",
  },
  {
    icon: ShieldCheck,
    title: "IRS-ready filings",
    body: "Form 2553 done correctly, on time, with the right effective date — no guesswork, no rejected elections.",
  },
  {
    icon: MapPin,
    title: "Every state covered",
    body: "State-specific payroll and tax registration guidance for all 50 states and DC, with direct links.",
  },
  {
    icon: Headset,
    title: "Help when you want it",
    body: "From a fully DIY guide to a done-for-you concierge filing — you control how hands-on we are.",
  },
];

const platformFeatures = [
  { icon: BookOpen, title: "Step-by-step guide", body: "A complete 8-step conversion roadmap plus a downloadable 12-page PDF." },
  { icon: FileCheck, title: "Interactive checklist", body: "Track your progress with saved state across devices." },
  { icon: Calculator, title: "Savings calculator", body: "Personalized S-corp savings and salary estimates." },
  { icon: ScrollText, title: "Document templates", body: "Everything you need for a clean, compliant election." },
  { icon: MapPin, title: "State directory", body: "Payroll & SUI registration links for all 51 jurisdictions." },
  { icon: FileSignature, title: "Done-for-you filing", body: "We prepare and file Form 2553 and set up your accounts." },
  { icon: Lock, title: "Secure document intake", body: "Encrypted upload and delivery between you and our team." },
  { icon: Sparkles, title: "AI guidance", body: "An on-page assistant answers your S-Corp questions instantly." },
];

const faqs = [
  {
    q: "What's the difference between an LLC and an S-Corp?",
    a: "An LLC is a legal business structure; an S-Corp is a tax election. By electing S-Corp status, your LLC can split income between a reasonable salary and distributions — and distributions aren't subject to the 15.3% self-employment tax, which is where the savings come from.",
  },
  {
    q: "How much can I actually save?",
    a: "It depends on your net profit and a reasonable salary for your role. Many owners with $80k+ in net profit save several thousand dollars a year. Use the free calculator above for a personalized estimate.",
  },
  {
    q: "Is there a deadline to elect S-Corp status?",
    a: "Generally Form 2553 must be filed within 2 months and 15 days of the start of the tax year you want the election to take effect — though late-election relief is often available. Our guide and specialists make sure you hit the right date.",
  },
  {
    q: "Which plan should I choose?",
    a: "Self-Guided is for confident DIY owners. Assisted adds expert document review and strategy calls. Full-Service is fully done-for-you — we file Form 2553 and set up your payroll and tax accounts. The calculator recommends a plan based on your numbers.",
  },
  {
    q: "Do you handle ongoing tax work too?",
    a: "Yes. Tax Planning & Annual Tax Preparation is available as a recurring add-on to any plan, with custom pricing on a quick consultation.",
  },
  {
    q: "Is this legal or tax advice?",
    a: "Our platform provides educational guidance and document preparation. For Full-Service and Assisted plans you work directly with tax professionals, but the content itself is educational and not a substitute for personalized legal advice.",
  },
];

const compareRows = [
  ["Step-by-step conversion guide", true, true, true],
  ["Interactive progress checklist", true, true, true],
  ["Document templates", true, true, true],
  ["1:1 document review", false, true, true],
  ["Strategy calls with a tax pro", false, true, true],
  ["We file Form 2553 for you", false, false, true],
  ["Post-conversion review", false, false, true],
  ["Tax planning & annual tax prep", "Add-on", "Add-on", "Add-on"],
  ["Payroll account setup", false, false, true],
  ["State & federal tax registration", false, false, true],
];

export default function Landing() {
  const navigate = useNavigate();

  const scrollToPricing = () =>
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });

  const scrollToCalc = () =>
    document.getElementById("calculator")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#F9F9F8] font-body">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 grain">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#114A31] bg-[#E6F0EB] px-4 py-2 rounded-full">
              <Leaf className="h-3.5 w-3.5" /> LLC → S-Corp made simple
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-medium text-[#1C1C1C]">
              Convert your LLC to an S-Corp,
              <span className="text-[#114A31]"> stress-free.</span>
            </h1>
            <p className="mt-6 text-lg text-[#595D5A] max-w-xl leading-relaxed">
              Learn it yourself, get expert review, or let us file everything and set up your payroll
              and tax accounts. Three ways to save on self-employment taxes — your pace, your budget.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                onClick={scrollToCalc}
                data-testid="hero-calc-btn"
                className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white px-8 py-6 text-base"
              >
                Calculate my savings <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                onClick={scrollToPricing}
                variant="outline"
                data-testid="hero-pricing-btn"
                className="rounded-full border-[#E5E5E5] hover:border-[#114A31] hover:bg-white px-8 py-6 text-base text-[#1C1C1C]"
              >
                See the plans
              </Button>
            </div>
            <button onClick={() => navigate("/register?plan=vault")} data-testid="hero-vault-btn"
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#114A31] hover:text-[#1A6B46] group">
              <span className="h-8 w-8 rounded-full bg-[#E6F0EB] flex items-center justify-center group-hover:bg-[#114A31] group-hover:text-white transition-colors"><Camera className="h-4 w-4" /></span>
              Not ready yet? Start free — snap receipts &amp; tax docs into your Document Vault
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>

          {/* Bento trust grid */}
          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 bg-white border border-[#E5E5E5] rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-2 text-[#595D5A] text-sm">
                  <TrendingUp className="h-4 w-4 text-[#114A31]" /> Estimated annual tax savings
                </div>
                <p className="mt-2 font-heading text-4xl font-bold text-[#114A31]">$8,400</p>
                <p className="text-xs text-[#595D5A] mt-1">Based on $120k net profit, after reasonable salary</p>
              </div>
              <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6 shadow-sm">
                <FileCheck className="h-6 w-6 text-[#114A31]" />
                <p className="mt-3 font-heading text-2xl font-bold text-[#1C1C1C]">8 steps</p>
                <p className="text-xs text-[#595D5A]">to a complete conversion</p>
              </div>
              <div className="bg-[#114A31] text-white rounded-2xl p-6 shadow-sm">
                <ShieldCheck className="h-6 w-6 text-[#7FE3AE]" />
                <p className="mt-3 font-heading text-2xl font-bold">IRS-ready</p>
                <p className="text-xs text-white/70">Form 2553 done right</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted by */}
      <section className="py-10 border-y border-black/5 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <p className="text-center text-xs tracking-[0.2em] uppercase font-bold text-[#595D5A]">
            Built on the tools owners already trust
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
            {partners.map((p) => (
              <div key={p.name} className="flex items-center gap-2 text-[#595D5A] opacity-70">
                <p.icon className="h-5 w-5" />
                <span className="font-heading font-semibold">{p.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-20 lg:py-28" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="max-w-2xl">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#114A31]">How it works</span>
            <h2 className="mt-3 font-heading text-3xl lg:text-4xl tracking-tight font-medium text-[#1C1C1C]">
              From "should I?" to "it's done" in four steps
            </h2>
            <p className="mt-4 text-[#595D5A] text-lg">
              A clear, predictable path — whether you do it yourself or hand it to us.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <div
                key={i}
                data-testid={`step-card-${i}`}
                className="relative bg-white border border-[#E5E5E5] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
              >
                <span className="font-heading text-sm font-bold text-[#7FA98E]">0{i + 1}</span>
                <div className="mt-3 h-11 w-11 rounded-xl bg-[#E6F0EB] flex items-center justify-center">
                  <s.icon className="h-5 w-5 text-[#114A31]" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold text-[#1C1C1C]">{s.title}</h3>
                <p className="mt-2 text-sm text-[#595D5A] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Savings Calculator */}
      <CalculatorSection onChoosePackage={scrollToPricing} />

      {/* Pricing */}
      <section id="pricing" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="max-w-2xl">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#114A31]">Pricing</span>
            <h2 className="mt-3 font-heading text-3xl lg:text-4xl tracking-tight font-medium text-[#1C1C1C]">
              Choose how much help you want
            </h2>
            <p className="mt-4 text-[#595D5A] text-lg">
              From a guided DIY path to a fully done-for-you filing. Upgrade any time.
            </p>
          </div>
          <div className="mt-12">
            <PricingCards />
          </div>

          {/* Comparison table */}
          <div className="mt-20 overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white">
            <table className="w-full text-sm" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-[#E5E5E5]">
                  <th className="text-left font-heading font-semibold text-[#1C1C1C] p-5">What's included</th>
                  <th className="font-heading font-semibold text-[#1C1C1C] p-5">Self-Guided</th>
                  <th className="font-heading font-semibold text-[#114A31] p-5">Assisted</th>
                  <th className="font-heading font-semibold text-[#1C1C1C] p-5">Full-Service</th>
                </tr>
              </thead>
              <tbody>
                {compareRows.map((row, i) => (
                  <tr key={i} className={i % 2 ? "bg-[#F9F9F8]" : "bg-white"}>
                    <td className="p-5 text-[#1C1C1C]">{row[0]}</td>
                    {[1, 2, 3].map((c) => (
                      <td key={c} className="p-5 text-center">
                        {typeof row[c] === "string" ? (
                          <span className="inline-block text-xs font-semibold text-[#92600A] bg-[#FEF3C7] px-2.5 py-1 rounded-full">
                            {row[c]}
                          </span>
                        ) : row[c] ? (
                          <Check className="h-5 w-5 text-[#114A31] inline" />
                        ) : (
                          <X className="h-5 w-5 text-[#D1D1D1] inline" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section id="why-us" className="py-20 lg:py-28 bg-white border-y border-black/5" data-testid="why-us">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="max-w-2xl">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#114A31]">Why ScorpPath</span>
            <h2 className="mt-3 font-heading text-3xl lg:text-4xl tracking-tight font-medium text-[#1C1C1C]">
              Built to save you money — and headaches
            </h2>
            <p className="mt-4 text-[#595D5A] text-lg">
              Everything you need to make the right call and file with confidence.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {whyUs.map((w, i) => (
              <div
                key={i}
                data-testid={`why-card-${i}`}
                className="flex gap-5 bg-[#F9F9F8] border border-[#E5E5E5] rounded-2xl p-6"
              >
                <div className="h-12 w-12 shrink-0 rounded-xl bg-[#114A31] flex items-center justify-center">
                  <w.icon className="h-5 w-5 text-[#7FE3AE]" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-[#1C1C1C]">{w.title}</h3>
                  <p className="mt-1.5 text-sm text-[#595D5A] leading-relaxed">{w.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform features */}
      <section id="features" className="py-20 lg:py-28" data-testid="platform-features">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="max-w-2xl">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#114A31]">Platform features</span>
            <h2 className="mt-3 font-heading text-3xl lg:text-4xl tracking-tight font-medium text-[#1C1C1C]">
              One platform for the whole conversion
            </h2>
            <p className="mt-4 text-[#595D5A] text-lg">
              Guidance, tools, and done-for-you services — all in one place.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {platformFeatures.map((f, i) => (
              <div
                key={i}
                data-testid={`feature-card-${i}`}
                className="bg-white border border-[#E5E5E5] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
              >
                <div className="h-11 w-11 rounded-xl bg-[#E6F0EB] flex items-center justify-center">
                  <f.icon className="h-5 w-5 text-[#114A31]" />
                </div>
                <h3 className="mt-4 font-heading text-base font-semibold text-[#1C1C1C]">{f.title}</h3>
                <p className="mt-1.5 text-sm text-[#595D5A] leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ongoing Services */}
      <section className="pb-8" data-testid="ongoing-services">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <div className="rounded-2xl border border-[#E5E5E5] bg-white overflow-hidden lg:grid lg:grid-cols-5">
            <div className="bg-[#0E3D28] text-white p-8 lg:p-10 lg:col-span-2 flex flex-col justify-center">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#7FE3AE]">Ongoing Services</span>
              <h2 className="mt-3 font-heading text-2xl lg:text-3xl font-medium tracking-tight">
                Tax Planning &amp; Annual Tax Preparation
              </h2>
              <p className="mt-3 text-white/70 text-sm leading-relaxed">
                Keep saving year after year. Recurring advisory that pairs perfectly with any conversion plan.
              </p>
              <div className="mt-5">
                <span className="font-heading text-2xl font-bold">Custom Pricing</span>
              </div>
              <Button
                onClick={() => window.dispatchEvent(new Event("scorppath:open-chat"))}
                data-testid="ongoing-consult-btn"
                className="mt-6 rounded-full bg-white text-[#0E3D28] hover:bg-[#E6F0EB] px-6 py-6 w-fit"
              >
                Schedule a Consultation <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="p-8 lg:p-10 lg:col-span-3">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  "Year-round tax planning",
                  "Annual business tax return",
                  "Quarterly planning sessions",
                  "S-Corp compliance review",
                  "Tax-saving strategy updates",
                  "CPA support",
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#1C1C1C]">
                    <Check className="h-5 w-5 text-[#114A31] shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-[#595D5A]">
                Available as a recurring add-on to any plan. Pricing tailored to your business on a quick call.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 lg:py-28" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6 sm:px-12">
          <div className="text-center">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#114A31]">FAQ</span>
            <h2 className="mt-3 font-heading text-3xl lg:text-4xl tracking-tight font-medium text-[#1C1C1C]">
              Questions, answered
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-b border-[#E5E5E5]"
              >
                <AccordionTrigger className="font-heading text-base font-semibold text-[#1C1C1C] hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#595D5A] text-sm leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <div className="bg-[#114A31] rounded-2xl p-12 lg:p-16 text-center shadow-xl">
            <h2 className="font-heading text-3xl lg:text-4xl font-medium text-white tracking-tight">
              Ready to keep more of what you earn?
            </h2>
            <p className="mt-4 text-white/70 max-w-xl mx-auto">
              Start with the self-guided path today, or let our specialists handle the whole conversion for you.
            </p>
            <Button
              onClick={() => navigate("/register")}
              data-testid="cta-signup-btn"
              className="mt-8 rounded-full bg-white text-[#114A31] hover:bg-[#E6F0EB] px-8 py-6 text-base"
            >
              Create your free account <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-black/5 py-10 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-lg bg-[#114A31] flex items-center justify-center">
              <Leaf className="h-4 w-4 text-white" />
            </span>
            <span className="font-heading font-semibold text-[#1C1C1C]">ScorpPath</span>
          </div>
          <p className="text-xs text-[#595D5A]">
            Educational guidance only — not legal or tax advice. © {new Date().getFullYear()} ScorpPath.
          </p>
        </div>
      </footer>
    </div>
  );
}
